import { useCallback, useEffect, useState } from 'react';
import type { AcousticCalibrationSnapshot } from '../lib/acoustic/calibrationTypes';
import {
  ACOUSTIC_CALIBRATION_UPDATED_EVENT,
  addCalibrationSnapshot,
  deleteCalibrationSnapshot,
  listCalibrationSnapshots
} from '../lib/acoustic/calibrationStorage';
import { useAuth } from './useAuth';
import { hydrateAcousticCalibrationFromServer } from '../lib/acoustic/acousticCalibrationSync';

export function useAcousticCalibrationStore() {
  const { user } = useAuth();
  const [snapshots, setSnapshots] = useState<AcousticCalibrationSnapshot[]>(() => listCalibrationSnapshots());

  const refresh = useCallback(() => {
    setSnapshots(listCalibrationSnapshots());
  }, []);

  useEffect(() => {
    if (!user) return;
    void hydrateAcousticCalibrationFromServer(user).then(refresh);
  }, [user, refresh]);

  useEffect(() => {
    const onUpdate = () => refresh();
    window.addEventListener(ACOUSTIC_CALIBRATION_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(ACOUSTIC_CALIBRATION_UPDATED_EVENT, onUpdate);
  }, [refresh]);

  const saveSnapshot = useCallback((snapshot: AcousticCalibrationSnapshot) => {
    setSnapshots(addCalibrationSnapshot(snapshot));
  }, []);

  const removeSnapshot = useCallback((id: string) => {
    setSnapshots(deleteCalibrationSnapshot(id));
  }, []);

  return { snapshots, saveSnapshot, removeSnapshot, refresh };
}
